import { type ClassValue, clsx } from "clsx";
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  differenceInYears,
  format,
  parse,
  parseISO,
} from "date-fns";
import { t } from "i18next";
import toast from "react-hot-toast";
import { twMerge } from "tailwind-merge";

import { Sex, SubjectDemographic } from "@binah/web-sdk";
import { OnboardingStep } from "services/dashboard/type";
import useAuthStore from "store/auth";
import { SortOrder, UserAttributes } from "../types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getBaseUrl = () => {
  // return process.env.REACT_APP_MX_DEV_API_BASE_URL || '';

  let baseURL = process.env.REACT_APP_MX_PROD_API_BASE_URL;
  if (window.location.hostname.indexOf("dev") >= 0) {
    baseURL = process.env.REACT_APP_MX_DEV_API_BASE_URL;
  }
  if (window.location.hostname.indexOf("stage") >= 0) {
    baseURL = process.env.REACT_APP_MX_STAGE_API_BASE_URL;
  }

  return baseURL || "";
};

export const getPublicEncryptionKey = () => {
  let baseURL = process.env.REACT_APP_AWS_PUBLIC_KEY;
  if (window.location.hostname.indexOf("dev") >= 0) {
    baseURL = process.env.REACT_APP_DEV_AWS_PUBLIC_KEY;
  }
  if (window.location.hostname.indexOf("stage") >= 0) {
    baseURL = process.env.REACT_APP_STAGE_AWS_PUBLIC_KEY;
  }

  return baseURL || "";
};

export const getAppBaseUrl = () => {
  let baseURL = process.env.REACT_APP_MOBILE_PROD_BASE_URL;
  if (window.location.hostname.indexOf("dev") >= 0) {
    baseURL = process.env.REACT_APP_MOBILE_DEV_BASE_URL;
  }
  if (window.location.hostname.indexOf("stage") >= 0) {
    baseURL = process.env.REACT_APP_MOBILE_STAGE_BASE_URL;
  }

  return baseURL || "";
};

export const getKMSKeys = () => {
  // return {
  //   accessKey: process.env.REACT_APP_DEV_KMS_ACCESS_KEY || '',
  //   secretAccessKey: process.env.REACT_APP_DEV_KMS_SECRET_ACCESS_KEY || '',
  //   decryptAccessKey: process.env.REACT_APP_DEV_DECRYPT_KMS_ACCESS_KEY || '',
  //   decryptSecretAccessKey:
  //     process.env.REACT_APP_DEV_KMS_DECRYPT_SECRET_ACCESS_KEY || '',
  //   encryptionARNKey: process.env.REACT_APP_MX_DEV_KMS_ENCRYPTION_ARN_KEY || '',
  //   decryptionARNKey: process.env.REACT_APP_MX_DEV_KMS_DECRYPTION_ARN_KEY || '',
  // };

  let accessKey = process.env.REACT_APP_PROD_KMS_ACCESS_KEY;
  let decryptAccessKey = process.env.REACT_APP_PROD_DECRYPT_KMS_ACCESS_KEY;
  let secretAccessKey = process.env.REACT_APP_PROD_KMS_SECRET_ACCESS_KEY;
  let decryptSecretAccessKey =
    process.env.REACT_APP_PROD_KMS_DECRYPT_SECRET_ACCESS_KEY;
  let encryptionARNKey = process.env.REACT_APP_MX_PROD_KMS_ENCRYPTION_ARN_KEY;
  let decryptionARNKey = process.env.REACT_APP_MX_PROD_KMS_DECRYPTION_ARN_KEY;

  if (window.location.hostname.indexOf("stage") >= 0) {
    accessKey = process.env.REACT_APP_STAGE_KMS_ACCESS_KEY;
    decryptAccessKey = process.env.REACT_APP_STAGE_DECRYPT_KMS_ACCESS_KEY;
    secretAccessKey = process.env.REACT_APP_STAGE_KMS_SECRET_ACCESS_KEY;
    decryptSecretAccessKey =
      process.env.REACT_APP_STAGE_KMS_DECRYPT_SECRET_ACCESS_KEY;
    encryptionARNKey = process.env.REACT_APP_MX_STAGE_KMS_ENCRYPTION_ARN_KEY;
    decryptionARNKey = process.env.REACT_APP_MX_STAGE_KMS_DECRYPTION_ARN_KEY;
  }

  if (window.location.hostname.indexOf("dev") >= 0) {
    accessKey = process.env.REACT_APP_DEV_KMS_ACCESS_KEY;
    decryptAccessKey = process.env.REACT_APP_DEV_DECRYPT_KMS_ACCESS_KEY;
    secretAccessKey = process.env.REACT_APP_DEV_KMS_SECRET_ACCESS_KEY;
    decryptSecretAccessKey =
      process.env.REACT_APP_DEV_KMS_DECRYPT_SECRET_ACCESS_KEY;
    encryptionARNKey = process.env.REACT_APP_MX_DEV_KMS_ENCRYPTION_ARN_KEY;
    decryptionARNKey = process.env.REACT_APP_MX_DEV_KMS_DECRYPTION_ARN_KEY;
  }

  return {
    accessKey: accessKey || "",
    secretAccessKey: secretAccessKey || "",
    decryptAccessKey: decryptAccessKey || "",
    decryptSecretAccessKey: decryptSecretAccessKey || "",
    encryptionARNKey: encryptionARNKey || "",
    decryptionARNKey: decryptionARNKey || "",
  };
};

export const getMilestoneTag = (name: string) => {
  const steps = useAuthStore.getState().onboardingSteps;
  return steps.find((step) => step.name === name)?.milestone_tag ?? "";
};

export const isMilestoneCompleted = (milestone: string) => {
  const onboarding = useAuthStore.getState().onboarding;

  if (!onboarding?.length) return false;

  return onboarding.some((item) => item.milestone_tag === milestone);
};

export const getCurrentMilestone = (step: OnboardingStep) => {
  const steps = useAuthStore.getState().onboardingSteps;
  const index = steps.findIndex((item) => item.name == step.name);
  return index === steps.length - 1 ? steps[index] : steps[index + 1];
};

export const getNextMilestone = () => {
  const currentMilestone = useAuthStore.getState().currentMilestone;
  const steps = useAuthStore.getState().onboardingSteps;
  const index = steps.findIndex((item) => item.name == currentMilestone);
  return steps[index + 1];
};

export const getDateTime = (date: string, dateFormat?: string) => {
  if (!date) return { date: "N/A", time: "N/A" };

  const parsedDate = parseISO(date);

  return {
    date: format(parsedDate, dateFormat || "dd MMMM yyyy"),
    time: format(parsedDate, "HH:mm"),
  };
};

export function formatAge(birthdate: string | undefined) {
  if (!birthdate) return "N/A";

  const parsedDate = parse(birthdate, "dd/MM/yyyy", new Date());
  return differenceInYears(new Date(), parsedDate);
}

export function getSubjectDemographic(
  attributes: UserAttributes | null
): SubjectDemographic | undefined {
  if (!attributes) return undefined;

  let age = 0,
    sex = Sex.MALE,
    weight = 0,
    height = 0;
  if (attributes?.gender)
    sex = attributes?.gender === "male" ? Sex.MALE : Sex.FEMALE;
  if (attributes?.birthdate) {
    const calculatedAge = formatAge(attributes.birthdate);
    age = calculatedAge !== "N/A" ? calculatedAge : 0;
  }
  if (attributes?.weight && attributes?.weight_unit)
    weight = getWeightInKg(attributes.weight, attributes.weight_unit);
  if (attributes?.height && attributes?.height_unit)
    height = getHeightInCm(attributes.height, attributes.height_unit);

  const isAgeValid = age >= 18 && age <= 110;
  const isWeightValid = weight >= 40 && weight <= 200;
  const isHeightValid = height >= 130 && height <= 230;

  if (isAgeValid && isWeightValid && isHeightValid) {
    return { sex, age, weight, height };
  }

  return undefined;
}

export function getWeightInKg(weight: string, unit: string) {
  if (unit === "lbs") {
    return Math.round(parseInt(weight) * 0.453592);
  }

  return parseInt(weight);
}

export function getHeightInCm(height: string, unit: string) {
  if (unit === "ft") {
    return Math.round(parseInt(height) * 30.48);
  }

  return parseInt(height);
}

export const getCurrentTimestamp = () => format(new Date(), "yyyy-MM-dd HH:mm");

export const getTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getElapsedSeconds = (time: string) => {
  const startTime = parseISO(time);

  return differenceInSeconds(new Date(), startTime);
};

export const isNumber = (value: unknown) => {
  return typeof value === "number" && !isNaN(value);
};

export const getTitleComment = (text: string | Record<string, unknown>) => {
  if (typeof text !== "string") {
    return {
      title: Object.keys(text)?.[0] || "",
      comment: "",
      choices:
        typeof Object.values(text)[0] === "string"
          ? ""
          : Object.values(text)[0],
    };
  }

  if (!text || typeof text !== "string") return { title: "", comment: "" };

  const splits = text.split("<comment>");
  const title = splits?.[0]?.trim() ?? "";
  const comment = splits?.[1]
    ? splits[1].replace(/<\/comment>/g, "").trim()
    : "";

  return { title, comment };
};

export function extractLabelAndComment(text: string): {
  label: string;
  comment: string;
} {
  if (!text.includes("<comment>")) {
    if (text.includes("-")) {
      const splitText = text.split("-");
      return { label: splitText[0].trim(), comment: splitText[1].trim() };
    }
    return { label: text, comment: "" };
  }
  const regex = /(.+?)\s*<comment>(.*?)<\/comment>/;

  const match = text.match(regex);

  if (match) {
    return { label: match[1].trim().replace(/-/g, ""), comment: match[2] };
  } else {
    return { label: text, comment: "" };
  }
}

export const extractFilenameFromURL = (url: string) => {
  const parsedUrl = new URL(url);
  const pathParts = parsedUrl.pathname.split("/");
  const filename = pathParts[pathParts.length - 1];
  return filename;
};

export const downloadFile = async (url: string | undefined) => {
  if (!url) return;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/pdf",
      },
    });

    const blob = await response.blob();
    const blobURL = window.URL.createObjectURL(blob);

    const fileName = extractFilenameFromURL(url);

    const a = document.createElement("a");
    a.href = blobURL;
    // a.download = `${fileName}.pdf`;
    a.download = `${fileName}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (error) {
    console.error(error);
    toast.error(t("failed_to_download_msg"));
  }
};

export const sortData = <T>({
  data,
  order,
  key,
}: {
  data: T[];
  order: SortOrder;
  key: keyof T;
}) => {
  const sortedData = [...data];
  if (order === "asc")
    sortedData.sort((a, b) => {
      return (
        new Date(a[key] as unknown as string).getTime() -
        new Date(b[key] as unknown as string).getTime()
      );
    });
  if (order === "desc")
    sortedData.sort((a, b) => {
      return (
        new Date(b[key] as unknown as string).getTime() -
        new Date(a[key] as unknown as string).getTime()
      );
    });

  return sortedData;
};

export const getWebcamResolution = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    const track = stream.getVideoTracks()[0];
    const settings = track.getSettings();
    const pixels = settings.width! * settings.height!;
    const megapixels = pixels / 1000000;
    return { resolution: megapixels, error: false };
  } catch (error) {
    console.error(error);
    return { resolution: 0, error: true };
  }
};

export const getSecondsRemaining = () => {
  const generatedTime = useAuthStore.getState().tokenGeneratedTime;
  const expiryTime = useAuthStore.getState().tokenExpiryTime;

  if (!generatedTime || !expiryTime) {
    return 0;
  }

  const tokenGenerateTime = new Date(generatedTime);
  const tokenExpiryTime = new Date(expiryTime);

  const now = new Date();

  const timeSinceLogin = (now.getTime() - tokenGenerateTime.getTime()) / 1000;

  const totalDuration = differenceInSeconds(tokenExpiryTime, tokenGenerateTime);

  const sessionSecondsRemaining = totalDuration - timeSinceLogin;

  return Math.floor(sessionSecondsRemaining);
};

export function isValidJSON(jsonString: string) {
  try {
    const parsed = JSON.parse(jsonString);
    return typeof parsed === "object" || Array.isArray(parsed);
  } catch (e) {
    return false;
  }
}

export function isValidExpression(expression: string | undefined): boolean {
  if (!expression) {
    return false;
  }
  try {
    eval(expression);
    return true;
  } catch (error) {
    return false;
  }
}

export const checkSpanishLocale = () => useAuthStore.getState().locale === "es";

export function getTimeAgoLabel(timestamp: string) {
  const now = new Date();
  const date = parseISO(timestamp);

  const minutes = differenceInMinutes(now, date);
  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = differenceInHours(now, date);
  if (hours < 24) {
    return `${hours}h`;
  }

  const days = differenceInDays(now, date);
  return `${days}d`;
}
